import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Refresh as RefreshIcon,
  RestartAlt as ResetIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  TextFields as TextIcon,
  Image as ImageIcon,
  Videocam as VideoIcon,
  RecordVoiceOver as VoiceIcon,
  AutoFixHigh as ProcessingIcon
} from '@mui/icons-material';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} role="tabpanel">
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Category icons mapping
const CATEGORY_ICONS = {
  text: <TextIcon />,
  image: <ImageIcon />,
  video: <VideoIcon />,
  voice: <VoiceIcon />,
  imageProcessing: <ProcessingIcon />
};

// Category colors
const CATEGORY_COLORS = {
  text: '#2196F3',
  image: '#4CAF50',
  video: '#FF9800',
  voice: '#9C27B0',
  imageProcessing: '#00BCD4'
};

export default function AIFeatureConfiguration() {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Global AI Feature Config
  const [globalConfig, setGlobalConfig] = useState({
    isFreeForAll: true,
    allTiersGetAllFeatures: true,
    platformFeePerGeneration: 0,
    monthlySubscriptionFee: 0,
    freeGenerationsPerMonth: null
  });

  // Company Overrides
  const [companyOverrides, setCompanyOverrides] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);

  // AI Usage Analytics
  const [usageStats, setUsageStats] = useState({
    totalGenerations: 0,
    totalCompanies: 0,
    totalRevenue: 0,
    generationsByType: {},
    generationsByProvider: {},
    topCompanies: []
  });

  // AI Providers
  const [providersByCategory, setProvidersByCategory] = useState({});
  const [categories, setCategories] = useState({});

  // Tier Limits Configuration
  const [tierLimitsConfig, setTierLimitsConfig] = useState({});
  const [defaultLimits, setDefaultLimits] = useState({});
  const [editingLimit, setEditingLimit] = useState(null); // { tier, category }
  const [editingValue, setEditingValue] = useState('');

  const loadConfiguration = useCallback(async () => {
    try {
      const response = await fetch('/api/ai-config/global');
      const data = await response.json();

      if (data.success) {
        setGlobalConfig(data.config);
        setCompanyOverrides(data.companyOverrides || []);
      } else {
        setError(data.error || 'Failed to load configuration');
      }
    } catch (err) {
      setError('Failed to load configuration: ' + err.message);
    }
  }, []);

  const loadUsageStats = useCallback(async () => {
    try {
      const response = await fetch('/api/ai-config/usage-stats');
      const data = await response.json();

      if (data.success) {
        setUsageStats(data.stats);
      }
    } catch (err) {
      console.error('Failed to load usage stats:', err);
    }
  }, []);

  const loadProviders = useCallback(async () => {
    try {
      const response = await fetch('/api/ai-config/providers');
      const data = await response.json();

      if (data.success) {
        setProvidersByCategory(data.providersByCategory || {});
        setCategories(data.categories || {});
      }
    } catch (err) {
      console.error('Failed to load providers:', err);
    }
  }, []);

  const loadTierLimitsConfig = useCallback(async () => {
    try {
      const response = await fetch('/api/ai-config/tier-limits-config');
      const data = await response.json();

      if (data.success) {
        setTierLimitsConfig(data.config || {});
        setDefaultLimits(data.defaults || {});
        setCategories(prev => ({ ...prev, ...data.categories }));
      }
    } catch (err) {
      console.error('Failed to load tier limits config:', err);
    }
  }, []);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      loadConfiguration(),
      loadUsageStats(),
      loadProviders(),
      loadTierLimitsConfig()
    ]);
    setLoading(false);
  }, [loadConfiguration, loadUsageStats, loadProviders, loadTierLimitsConfig]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  const handleToggleProvider = async (providerId, enabled) => {
    try {
      const response = await fetch(`/api/ai-config/providers/${providerId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled })
      });
      const data = await response.json();

      if (data.success) {
        // Update providersByCategory state
        setProvidersByCategory(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(categoryId => {
            if (updated[categoryId].providers) {
              updated[categoryId] = {
                ...updated[categoryId],
                providers: updated[categoryId].providers.map(p =>
                  p.id === providerId ? { ...p, enabled } : p
                )
              };
            }
          });
          return updated;
        });
        setSuccess(`Provider ${enabled ? 'enabled' : 'disabled'} successfully`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to update provider');
      }
    } catch (err) {
      setError('Failed to update provider: ' + err.message);
    }
  };

  const handleUpdateLimit = async (tier, category, newLimit) => {
    try {
      setSaving(true);
      const response = await fetch('/api/ai-config/tier-limits-config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tier, category, limit: parseInt(newLimit) })
      });
      const data = await response.json();

      if (data.success) {
        setTierLimitsConfig(prev => ({
          ...prev,
          [tier]: {
            ...prev[tier],
            [category]: data.config
          }
        }));
        setSuccess(`Limit updated: ${tier}/${category} = ${newLimit}`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to update limit');
      }
    } catch (err) {
      setError('Failed to update limit: ' + err.message);
    } finally {
      setSaving(false);
      setEditingLimit(null);
      setEditingValue('');
    }
  };

  const handleResetLimit = async (tier, category) => {
    try {
      setSaving(true);
      const response = await fetch('/api/ai-config/tier-limits-config/reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tier, category })
      });
      const data = await response.json();

      if (data.success) {
        setTierLimitsConfig(prev => ({
          ...prev,
          [tier]: {
            ...prev[tier],
            [category]: data.config
          }
        }));
        setSuccess(`Limit reset to default: ${data.defaultValue}`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to reset limit');
      }
    } catch (err) {
      setError('Failed to reset limit: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleResetAllLimits = async () => {
    if (!window.confirm('Are you sure you want to reset all limits to defaults?')) return;

    try {
      setSaving(true);
      const response = await fetch('/api/ai-config/tier-limits-config/reset-all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();

      if (data.success) {
        setTierLimitsConfig(data.config);
        setSuccess('All limits reset to defaults');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to reset all limits');
      }
    } catch (err) {
      setError('Failed to reset all limits: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGlobalConfig = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const response = await fetch('/api/ai-config/global', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          config: globalConfig,
          updatedBy: 'admin'
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Global AI configuration saved successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error || 'Failed to save configuration');
      }
    } catch (err) {
      setError('Failed to save configuration: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCompanyOverride = async (companyId, overrideData) => {
    try {
      const response = await fetch(`/api/ai-config/company-override/${companyId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(overrideData)
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Company override updated successfully');
        loadConfiguration();
        setOverrideDialogOpen(false);
      } else {
        setError(data.error || 'Failed to update company override');
      }
    } catch (err) {
      setError('Failed to update company override: ' + err.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Render provider card
  const renderProviderCard = (provider) => (
    <Card
      key={provider.id}
      variant="outlined"
      sx={{
        mb: 1,
        opacity: provider.enabled ? 1 : 0.6,
        borderColor: provider.enabled ? CATEGORY_COLORS[provider.category] : 'divider',
        borderWidth: provider.enabled ? 2 : 1
      }}
    >
      <CardContent sx={{ pb: 1, pt: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="subtitle1" component="div">
              {provider.displayName}
              {provider.isByok && (
                <Chip label="BYOK" size="small" sx={{ ml: 1 }} variant="outlined" />
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {provider.description}
            </Typography>
          </Box>
          <Switch
            checked={provider.enabled}
            onChange={(e) => handleToggleProvider(provider.id, e.target.checked)}
            color="primary"
          />
        </Box>
      </CardContent>
    </Card>
  );

  // Render tier limit cell
  const renderLimitCell = (tier, category) => {
    const config = tierLimitsConfig[tier]?.[category];
    const defaultValue = defaultLimits[tier]?.[category];
    const isEditing = editingLimit?.tier === tier && editingLimit?.category === category;

    if (isEditing) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TextField
            size="small"
            type="number"
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            inputProps={{ min: 0, style: { width: 60 } }}
            autoFocus
          />
          <IconButton
            size="small"
            color="success"
            onClick={() => handleUpdateLimit(tier, category, editingValue)}
            disabled={saving}
          >
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => { setEditingLimit(null); setEditingValue(''); }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: config?.isOverride ? 'bold' : 'normal',
            color: config?.isOverride ? 'primary.main' : 'text.primary'
          }}
        >
          {config?.limit ?? defaultValue ?? '-'}
        </Typography>
        {config?.isOverride && (
          <Chip label="Custom" size="small" color="primary" sx={{ height: 18, fontSize: 10 }} />
        )}
        <Tooltip title="Edit limit">
          <IconButton
            size="small"
            onClick={() => {
              setEditingLimit({ tier, category });
              setEditingValue(String(config?.limit ?? defaultValue ?? 0));
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {config?.isOverride && (
          <Tooltip title="Reset to default">
            <IconButton
              size="small"
              onClick={() => handleResetLimit(tier, category)}
              disabled={saving}
            >
              <ResetIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={loadAllData} disabled={loading}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2" component="span">
          <strong>Novo-hosted AI</strong> (monthly credits, pooled Gemini/OpenAI, BYOK defaults, bulk limits) is edited under{' '}
          <Button component={RouterLink} to="/configuration" size="small" variant="outlined" sx={{ mx: 0.5, verticalAlign: 'middle' }}>
            Configuration
          </Button>
          → tab <strong>Features &amp; Limits</strong> → section <strong>Hosted AI (Novo-provided)</strong>. This page is for legacy AI feature flags, providers, and usage limits.
        </Typography>
      </Alert>

      <Paper sx={{ mt: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Global Configuration" />
          <Tab label="AI Providers" />
          <Tab label="Usage Limits" />
          <Tab label="Company Overrides" />
          <Tab label="Usage Analytics" />
        </Tabs>

        {/* Global Configuration Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={globalConfig.isFreeForAll}
                    onChange={(e) => setGlobalConfig({ ...globalConfig, isFreeForAll: e.target.checked })}
                  />
                }
                label="Free for All Tiers (Recommended)"
              />
              <Typography variant="caption" display="block" color="text.secondary">
                When enabled, all AI features are free for all subscription tiers
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={globalConfig.allTiersGetAllFeatures}
                    onChange={(e) => setGlobalConfig({ ...globalConfig, allTiersGetAllFeatures: e.target.checked })}
                  />
                }
                label="All Tiers Get All Features"
              />
              <Typography variant="caption" display="block" color="text.secondary">
                When enabled, Free, Professional, and Enterprise tiers all have access to all AI features
              </Typography>
            </Grid>

            {!globalConfig.isFreeForAll && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Platform Fee Per Generation ($)"
                    type="number"
                    value={globalConfig.platformFeePerGeneration}
                    onChange={(e) => setGlobalConfig({ ...globalConfig, platformFeePerGeneration: parseFloat(e.target.value) })}
                    inputProps={{ step: 0.01, min: 0 }}
                    helperText="Fee charged per AI ad generation"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Monthly Subscription Fee ($)"
                    type="number"
                    value={globalConfig.monthlySubscriptionFee}
                    onChange={(e) => setGlobalConfig({ ...globalConfig, monthlySubscriptionFee: parseFloat(e.target.value) })}
                    inputProps={{ step: 0.01, min: 0 }}
                    helperText="Monthly fee for AI feature access"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Free Generations Per Month"
                    type="number"
                    value={globalConfig.freeGenerationsPerMonth || ''}
                    onChange={(e) => setGlobalConfig({ ...globalConfig, freeGenerationsPerMonth: e.target.value ? parseInt(e.target.value) : null })}
                    inputProps={{ min: 0 }}
                    helperText="Leave empty for unlimited"
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSaveGlobalConfig}
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} /> : 'Save Configuration'}
            </Button>
          </Box>
        </TabPanel>

        {/* AI Providers Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              AI Provider Visibility
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enable or disable individual AI providers. Disabled providers will not be visible to users in the iOS app.
              <br />
              <strong>BYOK</strong> = Bring Your Own Key (user provides their own API key)
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {Object.entries(providersByCategory).map(([categoryId, categoryData]) => (
              <Grid item xs={12} md={6} key={categoryId}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{
                      mr: 1,
                      color: CATEGORY_COLORS[categoryId],
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      {CATEGORY_ICONS[categoryId]}
                    </Box>
                    <Typography variant="h6">
                      {categoryData.name}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {categoryData.description}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {categoryData.providers?.map(renderProviderCard)}
                  {(!categoryData.providers || categoryData.providers.length === 0) && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No providers configured for this category
                    </Typography>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Usage Limits Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Tier-Based Usage Limits
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Configure monthly usage limits for each AI category per subscription tier.
                <br />
                <strong>Note:</strong> Limits are per <em>category</em>, not per provider. For example, if a user generates 5 images with DALL-E and 5 with Stable Diffusion,
                both count toward the <strong>Image</strong> category limit (total: 10).
                <br />
                <Chip label="Custom" size="small" color="primary" sx={{ ml: 0.5, mr: 0.5, height: 18 }} />
                indicates an overridden value (different from default).
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<ResetIcon />}
              onClick={handleResetAllLimits}
              disabled={saving}
            >
              Reset All to Defaults
            </Button>
          </Box>

          {/* Default Limits Info */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle2">Default Limits:</Typography>
            <Typography variant="body2">
              Free: Text {defaultLimits.free?.text}, Image {defaultLimits.free?.image}, Video {defaultLimits.free?.video}, Voice {defaultLimits.free?.voice}, Processing {defaultLimits.free?.imageProcessing} |
              Professional: Text {defaultLimits.professional?.text}, Image {defaultLimits.professional?.image}, Video {defaultLimits.professional?.video}, Voice {defaultLimits.professional?.voice}, Processing {defaultLimits.professional?.imageProcessing} |
              Enterprise: Text {defaultLimits.enterprise?.text}, Image {defaultLimits.enterprise?.image}, Video {defaultLimits.enterprise?.video}, Voice {defaultLimits.enterprise?.voice}, Processing {defaultLimits.enterprise?.imageProcessing}
            </Typography>
          </Alert>

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 150 }}>
                    Free Tier
                    <Typography variant="caption" display="block" color="text.secondary">
                      (Default: varies)
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 150 }}>
                    Professional
                    <Typography variant="caption" display="block" color="text.secondary">
                      (Default: varies)
                    </Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 150 }}>
                    Enterprise
                    <Typography variant="caption" display="block" color="text.secondary">
                      (Default: varies)
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(categories).map(([categoryId, categoryInfo]) => (
                  <TableRow key={categoryId} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{
                          mr: 1,
                          color: CATEGORY_COLORS[categoryId],
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          {CATEGORY_ICONS[categoryId]}
                        </Box>
                        <Box>
                          <Typography variant="body1">{categoryInfo.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            per month
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {renderLimitCell('free', categoryId)}
                    </TableCell>
                    <TableCell align="center">
                      {renderLimitCell('professional', categoryId)}
                    </TableCell>
                    <TableCell align="center">
                      {renderLimitCell('enterprise', categoryId)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Company Overrides Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Company-Specific Overrides</Typography>
            <IconButton onClick={loadConfiguration}>
              <RefreshIcon />
            </IconButton>
          </Box>

          {companyOverrides.length === 0 ? (
            <Alert severity="info">
              No company-specific overrides configured. Companies will use the global configuration.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'action.hover' }}>
                    <TableCell>Company Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Custom Pricing</TableCell>
                    <TableCell>Is Free</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {companyOverrides.map((override) => (
                    <TableRow key={override.companyId} hover>
                      <TableCell>{override.companyName || override.companyId}</TableCell>
                      <TableCell>
                        <Chip
                          label={override.isEnabled ? 'Enabled' : 'Disabled'}
                          color={override.isEnabled ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {override.customPricing ? (
                          <Chip label="Custom" color="primary" size="small" />
                        ) : (
                          <Chip label="Default" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={override.isFree ? 'Free' : 'Paid'}
                          color={override.isFree ? 'success' : 'warning'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedCompany(override);
                            setOverrideDialogOpen(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Usage Analytics Tab */}
        <TabPanel value={tabValue} index={4}>
          <Typography variant="h6" gutterBottom>Usage Analytics</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">{usageStats.totalGenerations.toLocaleString()}</Typography>
                <Typography variant="body2" color="text.secondary">Total Generations</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">{usageStats.totalCompanies.toLocaleString()}</Typography>
                <Typography variant="body2" color="text.secondary">Active Companies</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">${usageStats.totalRevenue.toLocaleString()}</Typography>
                <Typography variant="body2" color="text.secondary">Total Revenue</Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Generations by Type */}
          <Typography variant="subtitle1" sx={{ mt: 4, mb: 2 }}>Generations by Category</Typography>
          <Grid container spacing={2}>
            {Object.entries(usageStats.generationsByType || {}).map(([type, count]) => (
              <Grid item xs={6} md={2} key={type}>
                <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                  <Box sx={{ color: CATEGORY_COLORS[type], mb: 1 }}>
                    {CATEGORY_ICONS[type]}
                  </Box>
                  <Typography variant="h5">{count.toLocaleString()}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                    {type === 'imageProcessing' ? 'Image Processing' : type}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </TabPanel>
      </Paper>

      {/* Company Override Dialog */}
      <Dialog open={overrideDialogOpen} onClose={() => setOverrideDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Company Override</DialogTitle>
        <DialogContent>
          {selectedCompany ? (
            <Box sx={{ py: 1 }}>
              <Typography variant="subtitle1">{selectedCompany.companyName || selectedCompany.companyId}</Typography>
              <Typography variant="body2" color="text.secondary">Current status: {selectedCompany.isEnabled ? 'Enabled' : 'Disabled'}</Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">No company selected.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialogOpen(false)}>Close</Button>
          {selectedCompany && (
            <Button
              variant="contained"
              onClick={() => handleUpdateCompanyOverride(selectedCompany.companyId, selectedCompany)}
            >
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

